import { useCallback, useRef, useState } from 'react';

import { type Environment, fetchQuery, graphql } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { type ThemeColorModes, type ThemeState } from '@atlaskit/tokens';

import { updateThemeObjectWithDefaultValues, updateThemeWithDefaultValues } from '../utils';

import type { globalThemeManagementGetPreferenceQuery as GlobalThemeManagementGetPreferenceQueryType } from './__generated__/globalThemeManagementGetPreferenceQuery.graphql';

/**
 * Fetches the user's global (account-level) theme preference using Relay
 * @param relayEnvironment - The Relay environment
 * @param accountId - The user's account ID (returns undefined if null/undefined)
 * @returns The theme state object or undefined if not found, feature flag is disabled, or accountId is null/undefined
 */
export const getGlobalThemePreference: (
	relayEnvironment: Environment,
	accountId: string | null | undefined,
) => Promise<Partial<ThemeState> | undefined> = async (
	relayEnvironment: Environment,
	accountId: string | null | undefined,
): Promise<Partial<ThemeState> | undefined> => {
	if (!fg('shared-user-preferences') || !accountId) {
		return undefined;
	}

	const response = await fetchQuery<GlobalThemeManagementGetPreferenceQueryType>(
		relayEnvironment,
		graphql`
			query globalThemeManagementGetPreferenceQuery($accountId: ID!) {
				settings_userPreferencesGlobal(accountId: $accountId) {
					theme
				}
			}
		`,
		{
			accountId,
		},
	).toPromise();

	const themeString = response?.settings_userPreferencesGlobal?.theme;

	if (themeString === null || themeString === undefined) {
		return undefined;
	}

	return updateThemeWithDefaultValues(themeString);
};

/**
 * Base hook that manages lazy-loading of color mode preferences with a custom getter function.
 * Returns the current color mode state and a callback to trigger one-time loading on demand.
 *
 * This hook is useful for components that load color mode preferences
 * asynchronously when needed (e.g., when a user interacts with UI), avoiding
 * unnecessary API calls on initial render. The loading happens only once.
 *
 * When the shared-user-preferences feature flag is disabled or accountId is null/undefined,
 * this hook returns [undefined, undefined].
 *
 * @param loadGlobalTheme - Custom function to fetch global theme preferences
 * @param accountId - The user's account ID (returns undefined if null/undefined)
 * @returns A tuple of [colorMode, loadOnce] where:
 *   - colorMode: The loaded color mode, null if not yet loaded, or undefined if feature flag is disabled or accountId is null/undefined
 *   - loadOnce: Callback that accepts { isOpen: boolean } to trigger one-time loading when isOpen is true, or undefined if feature flag is disabled or accountId is null/undefined
 */
export const useGlobalColorModeStateWithCustomLoadFunction: (
	loadGlobalTheme: () => Promise<Partial<ThemeState> | undefined>,
	accountId: string | null | undefined,
) => [ThemeColorModes | null | undefined, ((params: { isOpen: boolean }) => void) | undefined] = (
	loadGlobalTheme: () => Promise<Partial<ThemeState> | undefined>,
	accountId: string | null | undefined,
): [ThemeColorModes | null | undefined, ((params: { isOpen: boolean }) => void) | undefined] => {
	// null value is needed to indicate that the color mode has not been loaded yet - this way the theme switcher should render the appearancePreferencesLink.
	const [colorMode, setColorMode] = useState<ThemeColorModes | null>(null);
	const hasLoaded = useRef(false);

	const loadOnce = useCallback(
		async ({ isOpen }: { isOpen: boolean }) => {
			if (isOpen && !hasLoaded.current) {
				hasLoaded.current = true;
				try {
					const theme = await loadGlobalTheme();
					if (theme !== undefined && theme !== null) {
						const themeWithDefaults = updateThemeObjectWithDefaultValues(theme);
						setColorMode(themeWithDefaults.colorMode ?? null); // colorMode should be always set after updateThemeObjectWithDefaultValues, ?? null here is for the type safety
					}
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('Failed to load theme preference:', error);
				}
			}
		},
		[loadGlobalTheme],
	);

	// Return early if feature flag is disabled or accountId is null/undefined (after all hooks are called)
	if (!fg('shared-user-preferences') || !accountId) {
		return [undefined, undefined];
	}

	return [colorMode, loadOnce];
};

/**
 * Hook that manages lazy-loading of global color mode preferences.
 * Returns the current global color mode state and a callback to trigger one-time loading on demand.
 *
 * This hook is useful for components that load the global color mode
 * asynchronously when needed (e.g., when a user interacts with UI), avoiding
 * unnecessary API calls on initial render. The loading happens only once.
 *
 * When the shared-user-preferences feature flag is disabled or accountId is null/undefined,
 * this hook returns [undefined, undefined].
 *
 * @param relayEnvironment - The Relay environment to use for fetching preferences
 * @param accountId - The user's account ID (returns undefined if null/undefined)
 * @returns A tuple of [globalColorMode, loadOnce] where:
 *   - globalColorMode: The loaded color mode, null if not yet loaded, or undefined if feature flag is disabled or accountId is null/undefined
 *   - loadOnce: Callback that accepts { isOpen: boolean } to trigger one-time loading when isOpen is true, or undefined if feature flag is disabled or accountId is null/undefined
 */
export const useGlobalColorModeState: (
	relayEnvironment: Environment,
	accountId: string | null | undefined,
) => [ThemeColorModes | null | undefined, ((params: { isOpen: boolean }) => void) | undefined] = (
	relayEnvironment: Environment,
	accountId: string | null | undefined,
): [ThemeColorModes | null | undefined, ((params: { isOpen: boolean }) => void) | undefined] => {
	const loadGlobalTheme = useCallback(
		() => getGlobalThemePreference(relayEnvironment, accountId),
		[relayEnvironment, accountId],
	);

	return useGlobalColorModeStateWithCustomLoadFunction(loadGlobalTheme, accountId);
};

/**
 * @deprecated Use useGlobalColorModeStateWithCustomLoadFunction instead.
 */
export const useColorModeState: (
	loadGlobalTheme: () => Promise<Partial<ThemeState> | undefined>,
	accountId: string | null | undefined,
) => [ThemeColorModes | null | undefined, ((params: { isOpen: boolean }) => void) | undefined] =
	useGlobalColorModeStateWithCustomLoadFunction;
